/**
 * Sealing a group key to the recovery key, which only a browser can do.
 *
 * The server holds no group key and never will, so it cannot wrap one to
 * anything — which means registering a recovery key covers nothing that already
 * exists. That is not a bug in registration; it is the property the whole
 * design rests on. It does mean the sealing has to happen somewhere with the
 * key open, and the only such place is a member's browser.
 *
 * Until this existed, nothing ever wrote an escrow row, so "groups covered"
 * read 0 however many keys were registered — a recovery system that had never
 * protected a single group and gave no sign of it.
 */

import { sealGroupKeyFor } from "../../lib/keyring.js";
import { trpcClient } from "../../lib/trpc.js";

export interface ActiveRecoveryKey {
  id: string;
  publicKey: string;
}

/**
 * The current recovery key, or null when there is none.
 *
 * Null is a running state, not a failure: an installation with no recovery key
 * works exactly as before, and every caller here treats it as nothing to do.
 * Errors are swallowed for the same reason — being unable to *ask* must never
 * stop somebody creating a group.
 */
export async function activeRecoveryKey(): Promise<ActiveRecoveryKey | null> {
  try {
    return await trpcClient.groups.recoveryKey.query();
  } catch {
    return null;
  }
}

/**
 * Seal one already-open group and store the copy.
 *
 * Returns whether it happened, so a caller covering several can report a real
 * number. A group whose key this browser cannot open is skipped rather than
 * treated as an error: it is the ordinary state for a group somebody has been
 * invited to but not yet opened, and there is nothing to fix.
 */
export async function escrowGroup(
  groupId: string,
  key: ActiveRecoveryKey,
): Promise<boolean> {
  try {
    const wrappedGroupKey = await sealGroupKeyFor(groupId, key.publicKey);
    await trpcClient.groups.escrow.mutate({
      groupId,
      recoveryKeyId: key.id,
      wrappedGroupKey,
    });
    return true;
  } catch {
    return false;
  }
}
